/**
 * Join Community Hook V2
 * 
 * Handles the complete flow for joining a community by purchasing membership NFT.
 * Uses the new CommunityMembershipV2 contract with built-in 95/5 fee split.
 * 
 * Features:
 * - Wallet authentication (including OAuth users)
 * - Network switching to target chain
 * - Balance verification
 * - Single transaction for payment + mint (contract handles fee split)
 * - Pre-mint mode support (payment + claim pre-minted NFT)
 * - One NFT per wallet enforcement
 * - Transaction monitoring
 * - Backend synchronization with on-chain verification
 * 
 * @module hooks/useJoinCommunity
 */

import { useState, useCallback, useMemo } from "react";
import { ethers } from "ethers";
import { useAccount } from "wagmi";

import useWalletAuth from "./useWalletAuth";
import useCheckNetwork from "./useCheckNetwork";
import { useRecordCommunityPurchaseMutation, useSyncMembershipFromChainMutation, useClaimFreeMembershipMutation } from "../redux/slices/api/communityApiSlice";
import {
  getChain,
  getNetworkCurrency,
  getNativeCurrencyTicker,
  getCurrencyTickerByAddress,
} from "../utils/currencies";
import notify from "../components/common/notify";
import { checkTokenExpiry } from "./useTokenExpiry";
import { accessToken } from "../utils/localData";

// Import the new V2 ABI
import CommunityMembershipV2ABI from "../data/abi/CommunityMembershipV2.json";

// ABI for ERC20 token approval
const ERC20_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)",
];

/**
 * Initial status for all join steps
 */
const initialJoinStatus = {
  connectWallet: { status: "idle", hidden: false, label: "Connect Wallet" },
  switchNetwork: { status: "idle", hidden: false, label: "Switch Network" },
  checkEligibility: { status: "idle", hidden: false, label: "Check Eligibility" },
  checkBalance: { status: "idle", hidden: false, label: "Check Balance" },
  approveToken: { status: "idle", hidden: true, label: "Approve Token" }, // Only for ERC20
  mintAndPay: { status: "idle", hidden: false, label: "Purchase Membership" },
  confirm: { status: "idle", hidden: false, label: "Confirm Membership" },
};

/**
 * Hook for joining a community
 * 
 * @param {Object} community - The community object to join
 * @param {Function} onSuccess - Callback on successful join
 * @param {Function} onError - Callback on error
 * @returns {Object} Join state and methods
 */
const useJoinCommunity = (community, { onSuccess, onError } = {}) => {
  const { address } = useAccount();
  const { ensureWalletReady } = useWalletAuth();
  const { switchNetwork } = useCheckNetwork();

  const effectiveNetwork = useMemo(
    () => community?.deploymentTx?.network || community?.blockchain,
    [community?.deploymentTx?.network, community?.blockchain]
  );

  // Get signer target chain metadata from the deployed network.
  const chainInfo = getChain({ network: effectiveNetwork });
  const targetChainId = chainInfo?.id;

  // API mutations
  const [recordPurchase] = useRecordCommunityPurchaseMutation();
  const [claimFree] = useClaimFreeMembershipMutation();
  const [syncMembershipFromChain] = useSyncMembershipFromChainMutation();

  // State
  const [joinStatus, setJoinStatus] = useState(initialJoinStatus);
  const [isJoining, setIsJoining] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [txHash, setTxHash] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Update status for one or more steps
   */
  const updateStatus = useCallback((updates) => {
    setJoinStatus((prev) => {
      const newStatus = { ...prev };
      updates.forEach(({ key, status, hidden, label }) => {
        if (newStatus[key]) {
          newStatus[key] = {
            ...newStatus[key],
            status: status ?? newStatus[key].status,
            hidden: hidden ?? newStatus[key].hidden,
            label: label ?? newStatus[key].label,
          };
        }
      });
      return newStatus;
    });
  }, []);

  /**
   * Reset all status to initial state
   */
  const resetStatus = useCallback(() => {
    setJoinStatus(initialJoinStatus);
    setIsJoining(false);
    setCurrentStep(null);
    setTxHash(null);
    setError(null);
  }, []);

  /**
   * Get currency token data for the community's payment currency
   */
  const currencyTokenData = useMemo(() => {
    if (!community || !effectiveNetwork) return null;
    return getNetworkCurrency(
      effectiveNetwork,
      community.saleSettings?.currency || getNativeCurrencyTicker(effectiveNetwork)
    );
  }, [community, effectiveNetwork]);

  /**
   * Check if using native token (ETH) or ERC20
   */
  const isNativeToken = useMemo(() => {
    return currencyTokenData?.isNativeToken ?? true;
  }, [currencyTokenData]);

  /**
   * Check if community uses pre-mint mode
   */
  const isPreMintMode = useMemo(() => {
    return community?.preMintMode ?? community?.nftDetails?.preMintMode ?? false;
  }, [community]);

  /**
   * Check if this is a free premint claim:
   * premint mode enabled and price is 0.
   * Only FunDeSci users with a completed profile may claim.
   */
  const isFreePremint = useMemo(() => {
    return (community?.preMintMode ?? community?.nftDetails?.preMintMode ?? false) &&
      (community?.saleSettings?.price ?? 0) === 0;
  }, [community]);

  /**
   * Main join function - executes the complete flow
   * 
   * The new V2 contract handles:
   * - 95% to creator, 5% to platform (built into contract)
   * - One NFT per wallet enforcement
   * - Pre-mint mode support
   * - Payment + mint in single transaction
   */
  const joinCommunity = useCallback(async () => {
    if (!community || !address) {
      notify("error", "Missing community data or wallet address");
      return;
    }

    setIsJoining(true);
    setError(null);
    resetStatus();

    try {
      // ============================================
      // Step 1: Ensure wallet is ready
      // ============================================
      setCurrentStep("connectWallet");
      updateStatus([{ key: "connectWallet", status: "loading" }]);

      const { ready, reason } = await ensureWalletReady("join this community");
      if (!ready) {
        // Wallet not connected - this should be handled before calling joinCommunity
        updateStatus([{ key: "connectWallet", status: "error" }]);
        setError("Please connect your wallet to join this community.");
        setIsJoining(false);
        if (onError) {
          onError(new Error("Wallet not connected"));
        }
        return;
      }

      updateStatus([{ key: "connectWallet", status: "success" }]);

      // ============================================
      // Step 2: Switch network if needed
      // ============================================
      setCurrentStep("switchNetwork");
      updateStatus([{ key: "switchNetwork", status: "loading" }]);

      const { isSwitched } = await switchNetwork(effectiveNetwork, () => {
        updateStatus([{ key: "switchNetwork", status: "loading" }]);
      });

      updateStatus([{ key: "switchNetwork", status: "success" }]);

      // Small delay to let network settle
      if (isSwitched) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Get fresh signer after network switch
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const userAddress = await signer.getAddress();

      const contractAddress = community.nftDetails?.contractAddress;
      if (!contractAddress || contractAddress === "0x" + "0".repeat(40)) {
        throw new Error("Community NFT contract not deployed. The community owner needs to publish the community first.");
      }

      // Verify contract has code (is actually deployed on-chain)
      const code = await provider.getCode(contractAddress);
      if (code === "0x" || code === "0x0") {
        throw new Error("Community NFT contract not found on blockchain. It may have been deployed on a different network.");
      }

      // Get the NFT contract instance (needed for both eligibility and balance checks)
      const nftContract = new ethers.Contract(
        contractAddress, 
        CommunityMembershipV2ABI, 
        signer
      );

      const paymentTokenAddress = await nftContract.paymentToken();
      const resolvedCurrency =
        getCurrencyTickerByAddress(effectiveNetwork, paymentTokenAddress) ||
        community.saleSettings?.currency ||
        getNativeCurrencyTicker(effectiveNetwork);
      const resolvedCurrencyData = getNetworkCurrency(effectiveNetwork, resolvedCurrency);
      const resolvedIsNativeToken = paymentTokenAddress === ethers.ZeroAddress;
      const resolvedDecimals = resolvedCurrencyData?.info?.decimals || resolvedCurrencyData?.decimals || 18;

      // ============================================
      // Step 3: Check eligibility (one per wallet)
      // ============================================
      setCurrentStep("checkEligibility");
      updateStatus([{ key: "checkEligibility", status: "loading" }]);

      // Check if user can mint (hasn't minted before and doesn't own one)
      const canMint = await nftContract.canMint(userAddress);
      if (!canMint) {
        throw new Error("You already have a membership NFT for this community. Each wallet can only purchase one membership.");
      }

      // Check if sale is active
      const saleActive = await nftContract.saleActive();
      if (!saleActive) {
        throw new Error("Community membership sales are currently paused.");
      }

      // Check if pre-mint mode first, then check appropriate supply
      const preMintMode = await nftContract.preMintMode();
      if (preMintMode) {
        // In premint mode, check available pre-minted tokens (not availableSupply)
        // availableSupply will be 0 when all tokens are pre-minted, but they may still be claimable
        const availablePreminted = await nftContract.availablePreminted();
        if (availablePreminted === 0n) {
          throw new Error("No pre-minted memberships available. The creator needs to mint more.");
        }
      } else {
        // In normal mint mode, check available supply
        const availableSupply = await nftContract.availableSupply();
        if (availableSupply === 0n) {
          throw new Error("This community is sold out. No more memberships available.");
        }
      }

      updateStatus([{ key: "checkEligibility", status: "success" }]);

      // ============================================
      // Step 4: Check balance (skipped for free premint claims)
      // ============================================
      // Read on-chain price; we use this for the transaction value (0 when free)
      const price = await nftContract.membershipPrice();

      // Determine whether this is a free premint claim
      const isFreeClaim = preMintMode && price === 0n;

      if (!isFreeClaim) {
        const chainInfo = getChain({ network: effectiveNetwork });
        const chainName = chainInfo?.name || effectiveNetwork;
        setCurrentStep("checkBalance");
        updateStatus([{ key: "checkBalance", status: "loading", label: `Check Balance on ${chainName}` }]);

        // Detect price decimal mismatch (for communities created with old buggy code)
        if (!resolvedIsNativeToken && resolvedDecimals !== 18) {
          const expectedPriceFromDB = community.saleSettings?.price || 0;
          if (expectedPriceFromDB > 0) {
            const expectedPriceInCorrectDecimals = ethers.parseUnits(
              String(expectedPriceFromDB),
              resolvedDecimals
            );
            const expectedPriceIn18Decimals = ethers.parseUnits(
              String(expectedPriceFromDB),
              18
            );

            if (price === expectedPriceIn18Decimals && price !== expectedPriceInCorrectDecimals) {
              const actualPriceDisplay = ethers.formatUnits(price, 18);
              throw new Error(
                `This community has a price configuration issue. The price is stored as ${actualPriceDisplay} ${resolvedCurrency} instead of ${expectedPriceFromDB} ${resolvedCurrency}. ` +
                `Please contact the community creator to update the price using the contract's setPrice() function with the correct value: ${expectedPriceInCorrectDecimals.toString()}`
              );
            }
          }
        }

        let userBalance;
        if (resolvedIsNativeToken) {
          userBalance = await provider.getBalance(userAddress);
        } else {
          if (!resolvedCurrencyData?.info?.contract || resolvedCurrencyData.info.contract === ethers.ZeroAddress) {
            throw new Error(
              `${resolvedCurrency} is not supported on ${effectiveNetwork}. Please contact the community creator to update the payment currency.`
            );
          }
          const tokenContract = new ethers.Contract(paymentTokenAddress, ERC20_ABI, signer);
          userBalance = await tokenContract.balanceOf(userAddress);
        }

        if (userBalance < price) {
          const needed = ethers.formatUnits(price, resolvedDecimals);
          const has = ethers.formatUnits(userBalance, resolvedDecimals);
          throw new Error(`Insufficient balance. Need ${needed} ${resolvedCurrency}, have ${has} ${resolvedCurrency}`);
        }

        updateStatus([{ key: "checkBalance", status: "success" }]);
      } else {
        // Free claim — hide the balance and approval steps
        updateStatus([
          { key: "checkBalance", status: "success", hidden: true },
          { key: "approveToken", status: "idle", hidden: true },
        ]);
      }

      // ============================================
      // Step 5: Approve token (ERC20 only, skipped for free claims)
      // ============================================
      if (!resolvedIsNativeToken && !isFreeClaim) {
        setCurrentStep("approveToken");
        updateStatus([{ key: "approveToken", status: "loading", hidden: false }]);

        // Validate that we have a contract address for this ERC20 token
        if (!resolvedCurrencyData?.info?.contract || resolvedCurrencyData.info.contract === ethers.ZeroAddress) {
          throw new Error(
            `${resolvedCurrency} is not supported on ${effectiveNetwork}. Please contact the community creator to update the payment currency.`
          );
        }
        
        const tokenContract = new ethers.Contract(
          paymentTokenAddress,
          ERC20_ABI,
          signer
        );

        // Check current allowance - approve to the NFT contract
        const currentAllowance = await tokenContract.allowance(userAddress, contractAddress);

        if (currentAllowance < price) {
          // Need to approve the NFT contract to spend tokens
          const approveTx = await tokenContract.approve(contractAddress, price);
          await approveTx.wait();
        }

        updateStatus([{ key: "approveToken", status: "success" }]);
      }

      // ============================================
      // Step 6: Purchase membership (payment + mint in one tx)
      // ============================================
      setCurrentStep("mintAndPay");
      updateStatus([{
        key: "mintAndPay",
        status: "loading",
        label: isFreeClaim ? "Claim Free Membership" : "Purchase Membership",
      }]);

      let mintTx;
      let mintReceipt;

      if (preMintMode) {
        // Pre-mint mode: pay (or free claim) and transfer a pre-minted NFT
        if (isFreeClaim) {
          // Price is 0 — call payAndClaimPreminted with zero value;
          // the contract's `msg.value >= membershipPrice` check passes (0 >= 0)
          mintTx = await nftContract.payAndClaimPreminted({ value: 0n });
        } else if (resolvedIsNativeToken) {
          mintTx = await nftContract.payAndClaimPreminted({ value: price });
        } else {
          mintTx = await nftContract.payAndClaimPremintedWithToken();
        }
      } else {
        // On-demand mode: Pay and mint new NFT
        if (resolvedIsNativeToken) {
          mintTx = await nftContract.mintMembership({ value: price });
        } else {
          mintTx = await nftContract.mintMembershipWithToken();
        }
      }

      mintReceipt = await mintTx.wait();
      setTxHash(mintTx.hash);

      // Parse token ID from events
      let tokenId = null;
      const iface = new ethers.Interface(CommunityMembershipV2ABI);
      
      for (const log of mintReceipt.logs) {
        try {
          const parsed = iface.parseLog({
            topics: log.topics,
            data: log.data,
          });
          
          // Look for Transfer or MembershipMinted or PremintedTokenClaimed events
          if (parsed?.name === "Transfer" && 
              parsed.args.to.toLowerCase() === userAddress.toLowerCase() &&
              parsed.args.from.toLowerCase() !== userAddress.toLowerCase()) {
            tokenId = parsed.args.tokenId.toString();
            break;
          }
          if (parsed?.name === "MembershipMinted" && 
              parsed.args.member.toLowerCase() === userAddress.toLowerCase()) {
            tokenId = parsed.args.tokenId.toString();
            break;
          }
          if (parsed?.name === "PremintedTokenClaimed" && 
              parsed.args.member.toLowerCase() === userAddress.toLowerCase()) {
            tokenId = parsed.args.tokenId.toString();
            break;
          }
        } catch (e) {
          // Not a matching event, continue
        }
      }

      if (!tokenId) {
        // Fallback: query contract for user's token
        try {
          const balance = await nftContract.balanceOf(userAddress);
          if (balance > 0n) {
            tokenId = await nftContract.tokenOfOwnerByIndex(userAddress, balance - 1n);
            tokenId = tokenId.toString();
          }
        } catch (e) {
          // Use estimate
          tokenId = (community.members?.length || 0) + 1;
        }
      }

      updateStatus([{ key: "mintAndPay", status: "success" }]);

      // ============================================
      // Step 7: Confirm with backend
      // ============================================
      setCurrentStep("confirm");
      updateStatus([{ key: "confirm", status: "loading" }]);

      // Check if token is still valid before making backend call
      // Blockchain transactions can take a while, so token may have expired
      const token = accessToken();
      const { isExpired } = checkTokenExpiry(token, 30); // 30 second buffer
      
      if (isExpired) {
        // Token expired during transaction - try public sync endpoint as fallback
        // The on-chain transaction was successful, membership is valid
        console.warn("Session expired during transaction. Attempting to sync membership from chain...");
        
        try {
          // Use the public sync endpoint that doesn't require auth
          const syncResult = await syncMembershipFromChain({
            id: community._id,
            body: {
              walletAddress: userAddress,
              txHash: mintTx.hash,
            },
          }).unwrap();
          
          if (syncResult.synced) {
            notify("success", "Welcome to the community! 🎉");
            updateStatus([{ key: "confirm", status: "success" }]);
            
            if (onSuccess) {
              onSuccess({
                tokenId: syncResult.tokenId || tokenId,
                txHash: mintTx.hash,
                community,
                syncedFromChain: true,
              });
            }
            return;
          }
        } catch (syncErr) {
          console.warn("Sync from chain failed, membership still valid on-chain:", syncErr);
        }
        
        // If sync also failed, still notify user their NFT is safe
        notify("warning", "Session expired but your membership NFT was minted successfully! Please refresh the page to see your membership.");
        updateStatus([{ key: "confirm", status: "success" }]);
        
        if (onSuccess) {
          onSuccess({
            tokenId,
            txHash: mintTx.hash,
            community,
            sessionExpired: true, // Flag to indicate session expired
          });
        }
        return;
      }

      if (isFreeClaim) {
        // Free premint: use the dedicated endpoint which validates FunDeSci profile
        await claimFree({
          id: community._id,
          body: {
            tokenId: parseInt(tokenId),
            txHash: mintTx.hash,
            walletAddress: userAddress,
          },
        }).unwrap();
      } else {
        await recordPurchase({
          id: community._id,
          body: {
            tokenId: parseInt(tokenId),
            txHash: mintTx.hash,
            walletAddress: userAddress,
            pricePaid: ethers.formatUnits(price, resolvedDecimals),
            currency: resolvedCurrency,
          },
        }).unwrap();
      }

      updateStatus([{ key: "confirm", status: "success" }]);

      // ============================================
      // Success!
      // ============================================
      notify("success", "Welcome to the community! 🎉");
      
      if (onSuccess) {
        onSuccess({
          tokenId,
          txHash: mintTx.hash,
          community,
        });
      }

    } catch (err) {
      console.error("Join community error:", err);
      
      // Parse error message from contract or API
      let errorMessage = err?.reason || err?.data?.message || err?.message || "Failed to join community";
      
      // Handle 401 authentication errors specifically
      // If this happens at the confirm step, the on-chain transaction was successful
      if (err?.status === 401 || errorMessage.includes("Authentication required") || errorMessage.includes("Token expired")) {
        // Check if we're at the confirm step - means blockchain tx succeeded
        if (currentStep === "confirm" && txHash) {
          console.warn("Session expired during backend confirmation. Blockchain transaction succeeded.");
          notify("warning", "Your membership NFT was minted successfully! Your session expired during confirmation. Please refresh the page and log in again to see your membership.");
          updateStatus([{ key: "confirm", status: "success" }]);
          
          if (onSuccess) {
            onSuccess({
              tokenId: null, // Unknown without backend confirmation
              txHash,
              community,
              sessionExpired: true,
            });
          }
          return;
        }
        errorMessage = "Session expired. Please log in again and retry.";
      }
      
      // Handle known contract errors
      if (errorMessage.includes("AlreadyMember") || errorMessage.includes("Already a member")) {
        errorMessage = "You already own a membership NFT for this community.";
      } else if (errorMessage.includes("SoldOut") || errorMessage.includes("Sold out")) {
        errorMessage = "This community is sold out.";
      } else if (errorMessage.includes("SaleNotActive")) {
        errorMessage = "Membership sales are currently paused.";
      } else if (errorMessage.includes("InsufficientPayment")) {
        errorMessage = "Payment amount is less than the membership price.";
      } else if (errorMessage.includes("NoPremintedTokensAvailable")) {
        errorMessage = "No pre-minted memberships available.";
      }
      
      setError(errorMessage);

      // Update current step to error
      if (currentStep) {
        updateStatus([{ key: currentStep, status: "error" }]);
      }

      // Handle specific errors
      if (err?.code === "ACTION_REJECTED" || err?.code === 4001) {
        notify("error", "Transaction rejected by user");
      } else if (errorMessage.includes("Insufficient balance")) {
        notify("error", errorMessage);
      } else {
        notify("error", errorMessage);
      }

      if (onError) {
        onError(err);
      }
    } finally {
      setIsJoining(false);
      setCurrentStep(null);
    }
  }, [
    community,
    address,
    ensureWalletReady,
    switchNetwork,
    effectiveNetwork,
    recordPurchase,
    claimFree,
    updateStatus,
    resetStatus,
    onSuccess,
    onError,
    currentStep,
  ]);

  /**
   * Computed: Check if community supports joining and why not
   */
  const canJoin = useMemo(() => {
    if (!community) return { allowed: false, reason: "Community not found" };
    
    // Must have deployed contract
    const hasContract = community.nftDetails?.contractAddress && 
      community.nftDetails.contractAddress !== "0x" + "0".repeat(40);
    
    if (!hasContract) {
      return { allowed: false, reason: "Community NFT contract not deployed yet" };
    }
    
    // Must have available supply
    const hasSupply = (community.totalSupply || community.nftDetails?.maxSupply || 0) > 
      (community.members?.length || 0);
    
    if (!hasSupply) {
      return { allowed: false, reason: "Community is sold out" };
    }
    
    // Must be published/active
    const isActive = community.state === "active" || community.published === true;
    
    if (!isActive) {
      return { allowed: false, reason: "Community is not active yet" };
    }
    
    return { allowed: true, reason: null };
  }, [community]);

  /**
   * Get price display info
   */
  const priceInfo = useMemo(() => {
    if (!community) return null;
    
    const price = community.saleSettings?.price || 0;
    const currency =
      getNetworkCurrency(effectiveNetwork, community.saleSettings?.currency)
        ? community.saleSettings?.currency
        : getNativeCurrencyTicker(effectiveNetwork);
    const isFree = price === 0 || price === "0";
    
    return {
      price,
      currency,
      isFree,
      formatted: isFree ? "Free" : `${price} ${currency}`,
      feeBreakdown: {
        creatorReceives: (price * 0.95).toFixed(6),
        platformFee: (price * 0.05).toFixed(6),
      },
    };
  }, [community, effectiveNetwork]);

  return {
    // State
    joinStatus,
    isJoining,
    currentStep,
    txHash,
    error,
    
    // Computed
    canJoin,
    priceInfo,
    isNativeToken,
    isPreMintMode,
    isFreePremint,
    targetChainId,
    
    // Actions
    joinCommunity,
    resetStatus,
    updateStatus,
  };
};

export default useJoinCommunity;
